import React, { useCallback } from 'react';
import styled from 'styled-components';
import { SuggestionOption } from 'common/header/GlobalSearch/types';
import { StaticPermissionsFn, useStaticPermissions } from 'common/auth/Authorize';
import { Icon, Select } from '@beeline.com/bp-design-system';
import { useHeaderIntl } from 'src/components/common/header/translations/useHeaderIntl';

interface GlobalSearchInputProps {
  options: SuggestionOption[];
  onOpen(): void;
  onClose(): void;
  onSearch(search: string, sitePermissions: StaticPermissionsFn): void;
  onChange(option: SuggestionOption['value']): void;
  loading: boolean;
}

export const GlobalSearchInput = ({
  options,
  onSearch,
  onOpen,
  loading,
  onClose,
  onChange
}: GlobalSearchInputProps) => {
  const intl = useHeaderIntl();

  const sitePermissions = useStaticPermissions();

  const handleSearch = useCallback(
    (search: string) => {
      onSearch(search, sitePermissions);
    },
    [onSearch, sitePermissions]
  );
  const handleClose = useCallback(
    (value: SuggestionOption['value']) => {
      onChange(value);
      handleSearch('');
    },
    [onChange, handleSearch]
  );

  return (
    <StyledInputWrapper>
      <StyledMuiSelect
        onOpen={onOpen}
        onSearch={handleSearch}
        onChange={handleClose}
        onBlur={onClose}
        dataTest="global-search"
        placeholder={intl.searchPlaceholderPlain}
        autoComplete
        searchMode
        disableClearable
        loading={loading}
        options={options}
        inputProps={{
          startAdornment: <Icon name="search" />
        }}
        menuProps={{
          noWrapSubLabel: true,
          group: true,
          showOptionsDuringLoading: true,
          iconSize: 'small',
          minWidth: 350
        }}
      />
    </StyledInputWrapper>
  );
};

const StyledMuiSelect = styled(Select)`
  &.MuiAutocomplete-root.single-select {

    .MuiInputBase-root {
      background-color: ${({ theme }) => theme.colorSurfaceVariant};
    }
    .MuiSvgIcon-root {
      display: none;
    }
    [data-icon-name] {
      margin-right: ${({ theme }) => theme.spacingSize.s};
    }
  }
` as typeof Select;

const StyledInputWrapper = styled.div`
  position: relative;
`;
